'use client';

/**
 * Free Lift week tracker — the feature-rich "no program, just train" home
 * widget. Summarizes the current calendar week from the user's freestyle
 * sessions: sessions done, total volume (lifting) + distance (cardio/ruck),
 * a 7-day trained-or-not strip, a muscle-group balance view, and an
 * optional weekly session goal. No program week/day, no nagging.
 */
import { useEffect, useMemo, useState } from 'react';
import { Dumbbell, Footprints, Flame, Target, Check, Pencil } from 'lucide-react';
import { muscleBalance, TRAINING_GROUPS, type TrainingGroup } from '@/lib/freeLiftHistory';

const DAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const GOAL_KEY = 'opmode-freelift-weekly-goal';

// Monday-based start of the current week (local time).
function weekStart(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const diff = (d.getDay() + 6) % 7; // 0=Mon … 6=Sun
  d.setDate(d.getDate() - diff);
  return d;
}

const GROUP_COLOR: Record<TrainingGroup, string> = {
  Push: 'bg-rose-500/70',
  Pull: 'bg-sky-500/70',
  Legs: 'bg-emerald-500/70',
  Core: 'bg-amber-500/70',
  Other: 'bg-slate-500/70',
};

export default function FreeLiftWeekTracker({ sessions }: { sessions: any[] }) {
  const start = weekStart();
  const week = (sessions || []).filter(s => {
    const dt = s?.date ? new Date(s.date) : null;
    return dt && !isNaN(dt.getTime()) && dt >= start;
  });

  // Weekly session goal (localStorage; 0 = unset). Editable inline.
  const [goal, setGoal] = useState(0);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(GOAL_KEY);
      if (raw) setGoal(parseInt(raw, 10) || 0);
    } catch { /* ignore */ }
  }, []);
  const saveGoal = (n: number) => {
    const v = Math.max(0, Math.min(14, n));
    setGoal(v);
    try { localStorage.setItem(GOAL_KEY, String(v)); } catch { /* ignore */ }
    setEditing(false);
  };

  let volume = 0;
  let meters = 0;
  const trainedDay = new Array(7).fill(false);
  for (const s of week) {
    const dt = new Date(s.date);
    trainedDay[(dt.getDay() + 6) % 7] = true;
    for (const ex of (s.exercises || [])) {
      for (const set of (ex.sets || [])) {
        if ((set.weight || 0) > 0 && (set.reps || 0) > 0 && set.completed !== false) volume += set.weight * set.reps;
      }
      if (typeof ex.actualDistance === 'number') meters += ex.actualDistance;
    }
  }
  const miles = meters / 1609.34;

  // Muscle balance across the week (sets per training group).
  const balance = useMemo(() => muscleBalance(week), [week]);
  const balanceTotal = TRAINING_GROUPS.reduce((s, g) => s + balance[g], 0);
  const neglected = TRAINING_GROUPS.filter(g => balance[g] === 0);

  const goalPct = goal > 0 ? Math.min(100, Math.round((week.length / goal) * 100)) : 0;

  return (
    <section className="rounded-xl border border-operator-steel/40 bg-operator-slate/60 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading text-sm font-bold uppercase tracking-wider text-white">This week</div>
        <div className="font-mono text-[11px] text-slate-500">{week.length} session{week.length === 1 ? '' : 's'}</div>
      </div>

      {/* Weekly goal */}
      <div className="mb-4 rounded-lg bg-operator-void/40 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Target className="w-3.5 h-3.5 text-operator-gold" />
            <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Weekly goal</span>
          </div>
          {editing ? (
            <div className="flex items-center gap-1">
              {[2, 3, 4, 5, 6].map(n => (
                <button
                  key={n}
                  onClick={() => saveGoal(n)}
                  className={`w-6 h-6 rounded text-[11px] font-bold ${goal === n ? 'bg-operator-gold text-operator-void' : 'bg-operator-void/60 text-slate-300 border border-operator-steel/40'}`}
                >
                  {n}
                </button>
              ))}
              {goal > 0 && (
                <button onClick={() => saveGoal(0)} className="ml-1 text-[10px] text-slate-500 hover:text-red-400">clear</button>
              )}
            </div>
          ) : (
            <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-operator-gold transition">
              {goal > 0 ? <><span className="tabular-nums text-white">{week.length}/{goal}</span> <Pencil className="w-3 h-3" /></> : <><span>Set goal</span> <Pencil className="w-3 h-3" /></>}
            </button>
          )}
        </div>
        {goal > 0 && (
          <div className="h-2 bg-operator-void rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${week.length >= goal ? 'bg-emerald-500' : 'bg-operator-gold'}`}
              style={{ width: `${goalPct}%` }}
            />
          </div>
        )}
        {goal > 0 && week.length >= goal && (
          <div className="mt-1.5 flex items-center gap-1 text-[11px] text-emerald-400">
            <Check className="w-3 h-3" /> Goal hit — nice work.
          </div>
        )}
      </div>

      {/* 7-day strip */}
      <div className="flex justify-between mb-4">
        {DAYS.map((d, i) => (
          <div key={i} className="flex flex-col items-center gap-1">
            <div className={`w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-bold ${trainedDay[i] ? 'bg-operator-gold/20 text-operator-gold border border-operator-gold/50' : 'bg-operator-void/40 text-slate-600 border border-operator-steel/30'}`}>
              {trainedDay[i] ? '✓' : d}
            </div>
          </div>
        ))}
      </div>

      {/* Muscle balance */}
      {balanceTotal > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Muscle balance</span>
            <span className="font-mono text-[10px] text-slate-500">{balanceTotal} sets</span>
          </div>
          {/* Stacked proportion bar */}
          <div className="flex h-2.5 rounded-full overflow-hidden bg-operator-void/60 mb-2">
            {TRAINING_GROUPS.map(g => balance[g] > 0 && (
              <div key={g} className={GROUP_COLOR[g]} style={{ width: `${(balance[g] / balanceTotal) * 100}%` }} title={`${g}: ${balance[g]} sets`} />
            ))}
          </div>
          {/* Legend */}
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {TRAINING_GROUPS.map(g => (
              <div key={g} className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-sm ${balance[g] > 0 ? GROUP_COLOR[g] : 'bg-slate-700'}`} />
                <span className={`text-[10px] ${balance[g] > 0 ? 'text-slate-300' : 'text-slate-600'}`}>{g} {balance[g]}</span>
              </div>
            ))}
          </div>
          {neglected.length > 0 && neglected.length < 4 && (
            <p className="mt-2 text-[11px] text-amber-300/80">
              Neglected this week: <span className="font-semibold">{neglected.join(', ')}</span>.
            </p>
          )}
        </div>
      )}

      {/* Totals */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-operator-void/40 py-2.5">
          <Dumbbell className="w-4 h-4 text-operator-gold mx-auto mb-1" />
          <div className="font-heading text-lg font-bold tabular-nums text-white leading-none">{volume >= 1000 ? `${(volume / 1000).toFixed(1)}k` : volume}</div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-1">lb volume</div>
        </div>
        <div className="rounded-lg bg-operator-void/40 py-2.5">
          <Footprints className="w-4 h-4 text-operator-gold mx-auto mb-1" />
          <div className="font-heading text-lg font-bold tabular-nums text-white leading-none">{miles >= 0.1 ? miles.toFixed(1) : '0'}</div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-1">miles</div>
        </div>
        <div className="rounded-lg bg-operator-void/40 py-2.5">
          <Flame className="w-4 h-4 text-operator-gold mx-auto mb-1" />
          <div className="font-heading text-lg font-bold tabular-nums text-white leading-none">{trainedDay.filter(Boolean).length}</div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-1">days active</div>
        </div>
      </div>
    </section>
  );
}
